import { inject, Injectable } from '@angular/core';
import {
    ENDPOINTS,
    isDefined,
    NotificationIdentifier,
    OnlineOnlyService,
    UriUtilitiesService,
} from '@evorait/pace-core';
import { Observable, throwError } from 'rxjs';
import { HistoricalValue } from '../types/historical-value';
import { map } from 'rxjs/operators';
import { OdataHistoricalValue } from './forms-odata.interface';
import { HistoricalValueUtilities } from '../utilities/historical-value-utilities';
import { NotificationInstanceToken } from '../core/interfaces/notification.interface';

@Injectable({
    providedIn: 'root',
})
export class HistoricalValuesService {
    private readonly onlineOnlyService = inject(OnlineOnlyService);
    private readonly uriUtilities = inject(UriUtilitiesService);
    private readonly notificationService = inject(NotificationInstanceToken);

    public loadAllHistoricalValuesForUniqueId(
        uniqueId: string,
        notificationId: NotificationIdentifier,
        formId: string,
    ): Observable<HistoricalValue[]> {
        const notification = this.notificationService.getNotificationStateByIdentifier(notificationId);
        if (!isDefined(notification)) {
            return throwError(
                () =>
                    new Error(
                        `HistoricalValuesService: loadAllHistoricalValuesForUniqueId: Could not get notification model for id '${JSON.stringify(notificationId)}'`,
                    ),
            );
        }

        const mainEquipment = this.notificationService.getMainEquipmentFromNotification(notification);
        if (!isDefined(mainEquipment)) {
            throw new Error(
                `HistoricalValuesService: loadAllHistoricalValuesForUniqueId: Fatal: no mainEquipment available for notification '${JSON.stringify(
                    notificationId,
                )}`,
            );
        }

        let query = ENDPOINTS.HistoricalValuesSet.toString();
        const filter = `UniqueId eq '${encodeURIComponent(uniqueId)}' and MatnrId eq '${encodeURIComponent(
            mainEquipment.matnr,
        )}' and SernrId eq '${encodeURIComponent(mainEquipment.sernr)}' and FormDocid eq '${encodeURIComponent(formId)}'`;

        query = this.uriUtilities.appendFilterValue(query, filter);

        return this.onlineOnlyService
            .get(query)
            .pipe(
                map((data: OdataHistoricalValue[]) =>
                    HistoricalValueUtilities.convertODataHistoricalValuesToState(data),
                ),
            );
    }
}
